import { useEffect } from 'react'
import { skipToken, useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Check } from 'lucide-react'
import { LoadingButton } from '@/components/loading-button'
import { QuoteBuilderApiError, useQuoteBuilderApi } from '../api/client'
import { applyQuoteChange, applyWorkflowChange, quoteQueryOptions, workflowQueryOptions } from '../api/queries'
import { formatRelativeTime } from '../ui/format'
import type { WorkflowTabProps } from '../workflow/types'
import { AiReview } from './ai-review'
import { aiReviewQueryKey, approveAiReview, reviewQueryKey, reviewQueryOptions, type QuoteAiReview } from './review-api'

/** The Review stage: the AI insights, and Sales's approval of them (USA-1333). */
export function ReviewTab({ quoteId, quote, active = true, onQuoteChange, onWorkflowChange }: WorkflowTabProps) {
  const api = useQuoteBuilderApi()
  const client = useQueryClient()
  const mutationKey = ['review-approval', quoteId]
  const approving = useIsMutating({ mutationKey })
  const workspace = useQuery({
    ...reviewQueryOptions(api, quoteId),
    enabled: active && approving === 0,
  })
  const data = workspace.data

  // A quote change clears the approval: read it again for the new revision.
  useEffect(() => {
    if (active && !approving && data && data.quote_revision < quote.revision) {
      void client.invalidateQueries({ queryKey: reviewQueryKey(quoteId) }, { cancelRefetch: false })
    }
  }, [active, approving, client, data, quote.revision, quoteId])

  async function refreshQuote() {
    const [nextQuote, nextWorkflow] = await Promise.all([
      client.fetchQuery({ ...quoteQueryOptions(api, quoteId), staleTime: 0 }),
      client.fetchQuery({ ...workflowQueryOptions(api, quoteId), staleTime: 0 }),
    ])
    applyQuoteChange(client, quoteId, nextQuote)
    applyWorkflowChange(client, quoteId, nextWorkflow)
    onQuoteChange(nextQuote)
    onWorkflowChange(nextWorkflow)
  }

  // The AI review's own read (the panel fetches and polls it): approval waits until it has landed.
  const aiReview = useQuery<QuoteAiReview>({ queryKey: aiReviewQueryKey(quoteId, quote.revision), queryFn: skipToken })

  const approve = useMutation({
    mutationKey,
    scope: { id: `review-approval:${quoteId}` },
    mutationFn: () => approveAiReview(api, quoteId, quote.revision),
    onMutate: () => client.cancelQueries({ queryKey: reviewQueryKey(quoteId) }),
    onSuccess: (next) => {
      client.setQueryData(reviewQueryKey(quoteId), next)
      // Approval completes the Review stage without changing the quote.
      void client
        .fetchQuery({ ...workflowQueryOptions(api, quoteId), staleTime: 0 })
        .then((workflow) => {
          applyWorkflowChange(client, quoteId, workflow)
          onWorkflowChange(workflow)
        })
        .catch(() => undefined)
    },
    onError: (error) => {
      if (error instanceof QuoteBuilderApiError && error.status === 412) {
        void client.invalidateQueries({ queryKey: reviewQueryKey(quoteId) })
        void refreshQuote().catch(() => undefined)
      }
    },
  })

  const current = data?.quote_revision === quote.revision
  const approved = current && data?.ai_review.approval_state === 'approved'
  const aiReady = aiReview.data?.revision === quote.revision && aiReview.data.status !== 'running'
  const failure = approve.error
  const error =
    failure instanceof QuoteBuilderApiError && failure.status === 412
      ? 'The quote changed. Check it again and approve.'
      : failure instanceof QuoteBuilderApiError && failure.code === 'ai_review_not_ready'
        ? 'Wait for the AI review to finish, then approve.'
        : failure
          ? 'Approval could not be saved. Try again.'
          : null

  const action = approved ? (
    <span
      className="flex items-center gap-1.5 text-sm font-medium text-emerald-700 dark:text-emerald-400"
      data-review-approval="approved"
    >
      <Check aria-hidden="true" className="size-4" />
      Approved
      {data?.ai_review.approved_at ? (
        <span className="font-normal text-muted-foreground">{formatRelativeTime(data.ai_review.approved_at)}</span>
      ) : null}
    </span>
  ) : (
    <LoadingButton
      className="bg-emerald-600 text-white hover:bg-emerald-700"
      disabled={!data || !current || approving > 0 || !aiReady}
      loading={approve.isPending}
      loadingText="Approving..."
      onClick={() => approve.mutate()}
      size="sm"
    >
      <Check aria-hidden="true" />
      Approve
    </LoadingButton>
  )

  return (
    <div className="flex min-w-0 flex-col gap-3" data-review-stage={quoteId}>
      {error ? (
        <p
          className="rounded-lg border border-red-200 bg-red-50 px-4 py-2 text-xs text-destructive dark:border-red-900 dark:bg-red-950/40"
          role="alert"
        >
          {error}
        </p>
      ) : null}
      <AiReview action={action} active={active} lines={quote.lines} quoteId={quoteId} revision={quote.revision} />
    </div>
  )
}
